import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, IMPERSONATE_STORAGE_KEY } from "@/lib/api";

const AuthContext = createContext(null);

const _writeStorage = (key, val) => {
  try {
    if (val == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, val);
  } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  // realUser = the actual session owner (admin when impersonating).
  // user     = effective identity (impersonated target if active; else realUser).
  // impersonation = { active, imp_id, imp_token, target: {user_id, email, name, role}, started_at } | null
  // There is NO UI-only "view as" mode — every View-As starts a real
  // impersonation session against a sandbox account, so backend authorization
  // and frontend gates uniformly honor the impersonated identity.
  const [realUser, setRealUser] = useState(null);
  const [user, setUser] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);

  const _applyImpToken = (tok) => {
    if (tok) {
      api.defaults.headers.common["X-Impersonate-Token"] = tok;
      _writeStorage(IMPERSONATE_STORAGE_KEY, tok);
    } else {
      delete api.defaults.headers.common["X-Impersonate-Token"];
      _writeStorage(IMPERSONATE_STORAGE_KEY, null);
    }
  };

  const _loadEffectiveUser = useCallback(async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  }, []);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      // 1) Probe without impersonation header to get real user
      delete api.defaults.headers.common["X-Impersonate-Token"];
      const real = await api.get("/auth/me");
      setRealUser(real.data);

      // 2) If admin, fetch active impersonation state from server
      if (real.data?.role === "admin") {
        try {
          const s = await api.get("/admin/impersonate/status");
          if (s.data?.active) {
            _applyImpToken(s.data.imp_token);
            setImpersonation({
              active: true, imp_id: s.data.imp_id,
              imp_token: s.data.imp_token, target: s.data.target,
              started_at: s.data.started_at,
            });
            await _loadEffectiveUser();
            return;
          }
        } catch { /* admin endpoint may transiently fail; continue */ }
      }
      _applyImpToken(null);
      setImpersonation(null);
      setUser(real.data);
    } catch {
      setRealUser(null);
      setUser(null);
      setImpersonation(null);
      _applyImpToken(null);
    } finally {
      setLoading(false);
    }
  }, [_loadEffectiveUser]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const _applyImpersonationResponse = useCallback(async (data) => {
    _applyImpToken(data.imp_token);
    setImpersonation({
      active: true,
      imp_id: data.imp_id,
      imp_token: data.imp_token,
      target: data.target,
      started_at: new Date().toISOString(),
    });
    await _loadEffectiveUser();
    return data;
  }, [_loadEffectiveUser]);

  const startImpersonation = useCallback(async (target_user_id, reason) => {
    const r = await api.post("/admin/impersonate/start", { target_user_id, reason });
    return _applyImpersonationResponse(r.data);
  }, [_applyImpersonationResponse]);

  // Unified View-As: starts a real impersonation against a sandbox account
  // for the given role, so every gate (frontend + backend) honors the target.
  const startViewAsRole = useCallback(async (role) => {
    if (!["user", "dealer"].includes(role)) {
      throw new Error("role must be 'user' or 'dealer'");
    }
    const r = await api.post(`/admin/impersonate/sandbox/${role}`);
    return _applyImpersonationResponse(r.data);
  }, [_applyImpersonationResponse]);

  const stopImpersonation = useCallback(async () => {
    try { await api.post("/admin/impersonate/stop"); } catch { /* tolerate */ }
    _applyImpToken(null);
    setImpersonation(null);
    setUser(realUser);
  }, [realUser]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* tolerate */ }
    _applyImpToken(null);
    setRealUser(null);
    setUser(null);
    setImpersonation(null);
    window.location.href = "/";
  };

  // Effective UI role is simply user.role (which IS the impersonated target
  // during view-as because impersonation is unified).
  const effectiveRole = useMemo(() => user?.role, [user]);

  const value = {
    user, setUser, realUser, loading, checkAuth, reload: checkAuth, logout,
    impersonation, startImpersonation, startViewAsRole, stopImpersonation,
    effectiveRole,
    isAdminReal: realUser?.role === "admin",
    isImpersonating: !!impersonation?.active,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
